import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';

interface ChapterMapping {
  id: string;
  variantName: string;
  canonicalName: string | null;
  createdAt: string;
  updatedAt: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginTop: '1rem',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    backgroundColor: '#f9fafb',
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  headerTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#0f2d1f',
    margin: 0,
  },
  toggleIcon: {
    fontSize: '0.75rem',
    color: '#6b7280',
  },
  body: {
    padding: '0.75rem',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid #e5e7eb',
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.75rem',
  },
  td: {
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  input: {
    width: '100%',
    padding: '0.3rem 0.5rem',
    fontSize: '0.8rem',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  btnPrimary: {
    padding: '0.25rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#1a6b3c',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnSecondary: {
    padding: '0.25rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '0.25rem',
  },
  btnDanger: {
    padding: '0.25rem 0.6rem',
    fontSize: '0.75rem',
    fontWeight: 500,
    color: '#d32f2f',
    backgroundColor: '#fff',
    border: '1px solid #fca5a5',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  nullValue: {
    fontStyle: 'italic',
    color: '#9ca3af',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.4rem 0.6rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    marginBottom: '0.5rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    color: '#6b7280',
    padding: '1rem',
    fontSize: '0.8rem',
  },
  actions: {
    whiteSpace: 'nowrap' as const,
  },
};

export function ChapterMapConfig() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [mappings, setMappings] = useState<ChapterMapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add form state
  const [newVariant, setNewVariant] = useState('');
  const [newCanonical, setNewCanonical] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVariant, setEditVariant] = useState('');
  const [editCanonical, setEditCanonical] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Delete confirmation state
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient<ChapterMapping[]>('/order-import/chapter-map');
      setMappings(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to load chapter mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isExpanded && mappings.length === 0 && !loading) {
      fetchMappings();
    }
  }, [isExpanded, mappings.length, loading, fetchMappings]);

  const handleAdd = async () => {
    if (!newVariant.trim()) return;

    setIsAdding(true);
    setError(null);
    try {
      await apiClient('/order-import/chapter-map', {
        method: 'POST',
        body: {
          variantName: newVariant.trim(),
          canonicalName: newCanonical.trim() || null,
        },
      });
      setNewVariant('');
      setNewCanonical('');
      await fetchMappings();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to add mapping');
    } finally {
      setIsAdding(false);
    }
  };

  const handleEditStart = (mapping: ChapterMapping) => {
    setEditingId(mapping.id);
    setEditVariant(mapping.variantName);
    setEditCanonical(mapping.canonicalName || '');
    setDeletingId(null);
  };

  const handleEditSave = async () => {
    if (!editingId || !editVariant.trim()) return;

    setIsSaving(true);
    setError(null);
    try {
      await apiClient(`/order-import/chapter-map/${editingId}`, {
        method: 'PATCH',
        body: {
          variantName: editVariant.trim(),
          canonicalName: editCanonical.trim() || null,
        },
      });
      setEditingId(null);
      await fetchMappings();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to update mapping');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
  };

  const handleDeleteConfirm = async (id: string) => {
    setError(null);
    try {
      await apiClient(`/order-import/chapter-map/${id}`, {
        method: 'DELETE',
      });
      setDeletingId(null);
      await fetchMappings();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr?.message || 'Failed to delete mapping');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  return (
    <div style={styles.container}>
      <div
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label="Toggle Chapter Normalization Map"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <h4 style={styles.headerTitle}>Chapter Normalization Map</h4>
        <span style={styles.toggleIcon}>{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <div style={styles.body}>
          {error && <div style={styles.error}>{error}</div>}

          {loading ? (
            <div style={styles.emptyState}>Loading mappings...</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Variant Name</th>
                  <th style={styles.th}>Canonical Name</th>
                  <th style={{ ...styles.th, width: '120px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Add new mapping row */}
                <tr>
                  <td style={styles.td}>
                    <input
                      style={styles.input}
                      type="text"
                      value={newVariant}
                      onChange={(e) => setNewVariant(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, handleAdd)}
                      placeholder="Variant name"
                      disabled={isAdding}
                      aria-label="New variant name"
                    />
                  </td>
                  <td style={styles.td}>
                    <input
                      style={styles.input}
                      type="text"
                      value={newCanonical}
                      onChange={(e) => setNewCanonical(e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, handleAdd)}
                      placeholder="Canonical name (empty = null)"
                      disabled={isAdding}
                      aria-label="New canonical name"
                    />
                  </td>
                  <td style={{ ...styles.td, ...styles.actions }}>
                    <button
                      style={styles.btnPrimary}
                      onClick={handleAdd}
                      disabled={isAdding || !newVariant.trim()}
                      aria-label="Add mapping"
                    >
                      {isAdding ? '...' : 'Add'}
                    </button>
                  </td>
                </tr>

                {/* Existing mappings */}
                {mappings.map((mapping) => (
                  <tr key={mapping.id}>
                    {editingId === mapping.id ? (
                      <>
                        <td style={styles.td}>
                          <input
                            style={styles.input}
                            type="text"
                            value={editVariant}
                            onChange={(e) => setEditVariant(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, handleEditSave)}
                            disabled={isSaving}
                            aria-label="Edit variant name"
                          />
                        </td>
                        <td style={styles.td}>
                          <input
                            style={styles.input}
                            type="text"
                            value={editCanonical}
                            onChange={(e) => setEditCanonical(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, handleEditSave)}
                            placeholder="Empty = null"
                            disabled={isSaving}
                            aria-label="Edit canonical name"
                          />
                        </td>
                        <td style={{ ...styles.td, ...styles.actions }}>
                          <button
                            style={styles.btnPrimary}
                            onClick={handleEditSave}
                            disabled={isSaving || !editVariant.trim()}
                            aria-label="Save mapping"
                          >
                            {isSaving ? '...' : 'Save'}
                          </button>
                          <button
                            style={styles.btnSecondary}
                            onClick={handleEditCancel}
                            disabled={isSaving}
                            aria-label="Cancel editing"
                          >
                            Cancel
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={styles.td}>{mapping.variantName}</td>
                        <td style={styles.td}>
                          {mapping.canonicalName === null ? (
                            <span style={styles.nullValue}>none</span>
                          ) : (
                            mapping.canonicalName
                          )}
                        </td>
                        <td style={{ ...styles.td, ...styles.actions }}>
                          {deletingId === mapping.id ? (
                            <>
                              <button
                                style={styles.btnDanger}
                                onClick={() => handleDeleteConfirm(mapping.id)}
                                aria-label="Confirm delete"
                              >
                                Confirm
                              </button>
                              <button
                                style={styles.btnSecondary}
                                onClick={() => setDeletingId(null)}
                                aria-label="Cancel delete"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                style={styles.btnSecondary}
                                onClick={() => handleEditStart(mapping)}
                                aria-label={`Edit mapping ${mapping.variantName}`}
                              >
                                Edit
                              </button>
                              <button
                                style={styles.btnDanger}
                                onClick={() => setDeletingId(mapping.id)}
                                aria-label={`Delete mapping ${mapping.variantName}`}
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                ))}

                {mappings.length === 0 && (
                  <tr>
                    <td colSpan={3} style={styles.emptyState}>
                      No chapter mappings configured yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
